import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { PrinterStatus } from 'garaje/models/abstract/printer';
import type PrinterModel from 'garaje/models/abstract/printer';
import type { PrinterReachability } from 'garaje/utils/printer-reachability';

interface PrinterInfoConnectionStatusArgs {
  goToIPadPage: () => unknown;
  printer: PrinterModel;
  // Pass `reachability` to enable the failover-aware pill treatment. When omitted,
  // the component renders today's UI (pure `printer.printerStatus` enum).
  reachability?: PrinterReachability;
}

export default class PrinterInfoConnectionStatus extends Component<PrinterInfoConnectionStatusArgs> {
  @service declare router: RouterService;

  get isReachableViaFailover(): boolean {
    return this.args.reachability?.reachableViaFailover === true;
  }

  get showGoodPill(): boolean {
    if (this.args.printer.printerStatus === PrinterStatus.OPERATIONAL) return true;
    // Promote offline / not-in-use to "Operational" when the printer is reachable
    // via failover. checkIpads keeps its yellow pill — we still want the user to
    // know a primary iPad is misbehaving.
    if (!this.isReachableViaFailover) return false;
    const status = this.args.printer.printerStatus;
    return status === PrinterStatus.OFFLINE || status === PrinterStatus.NOT_IN_USE;
  }

  get showFailoverSubtext(): boolean {
    return this.showGoodPill && this.isReachableViaFailover;
  }

  get notConnectedDevicesCount(): number {
    const devices = <number>this.args.printer.devices.length;
    const connectedOnlineDevices = this.args.printer.connectedOnlineDevices.length;
    return devices - connectedOnlineDevices;
  }

  get displayPrinterDetails(): boolean {
    const details = this.args.printer.status;
    const status = this.args.printer.printerStatus;
    return !!details && details.toLowerCase() !== 'connected' && !['checkIpads', 'notInUse'].includes(status);
  }

  @action
  transitToIPadPage(): void {
    const goToIPadPage = this.args.goToIPadPage;
    if (goToIPadPage) {
      goToIPadPage();
    } else {
      void this.router.transitionTo('visitors.devices.ipads');
    }
  }
}
